import { Box, Paper, Stack, Typography, CircularProgress, Button, ButtonGroup } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import moment, { Moment } from 'moment';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceArea
} from 'recharts';
import { selectCities } from '../../redux/slices/citiesSlice';
import { selectAuth } from '../../redux/slices/authSlice';
import { selectAdmins } from '../../redux/slices/adminsSlice';
import usePermissions from '../../hooks/usePermissions';
import axios from '../../resources/axios';
import DateRangePicker from '../../components/DateRangePicker';
import { DateRange } from '../../types/dateRange';

interface ContractCountData {
  count: number;
  cityId: string;
  cityName: string;
  date: string;
}

interface BalanceHistoryData {
  count: number;
  totalAmount: number;
  cityId: string;
  cityName: string;
  type: string;
  date: string;
}

interface ChartData {
  date: string;
  trips?: number;
  topUps?: number;
  spending?: number;
  temperature?: number;
  windSpeed?: number;
  [cityName: string]: number | string | undefined;
}

type PeriodType = '7days' | '30days' | 'custom';

// Function to fetch weather data from Open-Meteo (free, no API key required)
const fetchWeatherData = async (lat: number, lon: number, from: string, to: string): Promise<Map<string, { temperature: number, windSpeed: number }>> => {
  const weatherMap = new Map<string, { temperature: number, windSpeed: number }>();
  
  try {
    const startDate = moment(from).format('YYYY-MM-DD');
    const endDate = moment(to).format('YYYY-MM-DD');
    const daysDiff = moment(to).diff(moment(from), 'days') + 1;
    
    console.log('Fetching weather data from Open-Meteo:', { 
      lat, 
      lon, 
      from: startDate, 
      to: endDate, 
      days: daysDiff
    });

    // Limit to 60 days max
    if (daysDiff > 60) {
      console.warn('Date range too large for weather API (max 60 days)');
      return weatherMap;
    }

    // Fetch weather data from Open-Meteo Archive API
    const url = `https://archive-api.open-meteo.com/v1/archive?latitude=${lat}&longitude=${lon}&start_date=${startDate}&end_date=${endDate}&daily=temperature_2m_max,wind_speed_10m_max&timezone=auto`;
    
    const response = await fetch(url);
    
    if (response.ok) {
      const data = await response.json();
      
      if (data.daily && data.daily.time) {
        const dates = data.daily.time;
        const temperatures = data.daily.temperature_2m_max;
        const windSpeeds = data.daily.wind_speed_10m_max;
        
        dates.forEach((date: string, index: number) => {
          const temp = temperatures[index];
          const wind = windSpeeds[index];
          
          if (temp !== null && wind !== null) {
            weatherMap.set(date, {
              temperature: Math.round(temp * 10) / 10,
              windSpeed: Math.round(wind * 10) / 10
            });
          }
        });
        
        console.log(`✓ Loaded weather data for ${weatherMap.size} days`);
      }
    } else {
      console.error('Weather API error:', response.status, await response.text());
    }
  } catch (error) {
    console.error('Error fetching weather data:', error);
  }

  return weatherMap;
};

const DailyLineChart = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const cities = useSelector(selectCities);
  const auth = useSelector(selectAuth);
  const adminInfo = useSelector(selectAdmins);
  const checkPermission = usePermissions();

  const [chartData, setChartData] = useState<ChartData[]>([]);
  const [loading, setLoading] = useState(false);
  const [periodType, setPeriodType] = useState<PeriodType>('7days');
  const [customPeriod, setCustomPeriod] = useState<DateRange<Moment | null>>([null, null]);
  const [hiddenLines, setHiddenLines] = useState<Set<string>>(new Set());

  const isSuperAdmin = useMemo(() => {
    return auth.info?.type === 'superAdmin';
  }, [auth.info?.type]);

  const currentCityId = useMemo(() => {
    return adminInfo.currentCity;
  }, [adminInfo.currentCity]);

  // Calculate date range based on period type
  const dateRange = useMemo(() => {
    const yesterday = moment().subtract(1, 'day').endOf('day');
    
    if (periodType === 'custom' && customPeriod[0] && customPeriod[1]) {
      // For custom period, limit end date to yesterday to avoid incomplete data
      const endDate = moment(customPeriod[1]).isAfter(yesterday) 
        ? yesterday 
        : moment(customPeriod[1]).endOf('day');
      
      return {
        from: moment(customPeriod[0]).utc().startOf('day').toISOString(),
        to: endDate.utc().toISOString()
      };
    } else if (periodType === '30days') {
      // Last 30 full days ending yesterday
      return {
        from: moment().subtract(30, 'days').utc().startOf('day').toISOString(),
        to: yesterday.utc().toISOString()
      };
    } else {
      // Last 7 full days ending yesterday
      return {
        from: moment().subtract(7, 'days').utc().startOf('day').toISOString(),
        to: yesterday.utc().toISOString()
      };
    }
  }, [periodType, customPeriod]);

  // Get chart title based on period
  const chartTitle = useMemo(() => {
    const yesterday = moment().subtract(1, 'day').endOf('day');
    
    if (periodType === 'custom' && customPeriod[0] && customPeriod[1]) {
      // Calculate actual end date (limited to yesterday)
      const endDate = moment(customPeriod[1]).isAfter(yesterday) 
        ? yesterday 
        : moment(customPeriod[1]);
      
      const days = endDate.diff(moment(customPeriod[0]), 'days') + 1;
      return t('stats.chartTitleCustom', { days });
    } else if (periodType === '30days') {
      return t('stats.chartTitle30Days');
    } else {
      return t('stats.chartTitle7Days');
    }
  }, [periodType, customPeriod]);

  useEffect(() => {
    const fetchDailyData = async () => {
      if (!checkPermission('contracts') || !currentCityId) return;

      setLoading(true);
      
      try {
        // Get city coordinates for weather API
        const currentCity = cities.items.find(item => item._id === currentCityId);
        const cityLat = currentCity?.lat;
        const cityLon = currentCity?.lon;

        // Fetch trips, balance, and weather data in parallel
        const [tripsResponse, balanceResponse, weatherData] = await Promise.all([
          axios.get('/statistics/contracts_count_by_days', {
            params: {
              cities: currentCityId,
              dateFrom: dateRange.from,
              dateTo: dateRange.to
            }
          }),
          axios.get('/statistics/balance_history_count_by_days', {
            params: {
              cities: currentCityId,
              dateFrom: dateRange.from,
              dateTo: dateRange.to
            }
          }),
          // Fetch weather data if coordinates are available
          cityLat && cityLon ? fetchWeatherData(cityLat, cityLon, dateRange.from, dateRange.to) : Promise.resolve(new Map())
        ]);

        const tripsData: ContractCountData[] = tripsResponse.data?.data || [];
        const balanceData: BalanceHistoryData[] = balanceResponse.data?.data || [];
        
        // Transform data for chart
        const dateMap = new Map<string, ChartData>();
        
        // Add trips data
        tripsData.forEach(item => {
          const dateKey = moment(item.date).format('YYYY-MM-DD');
          
          if (!dateMap.has(dateKey)) {
            dateMap.set(dateKey, { date: dateKey, trips: 0, topUps: 0, spending: 0 });
          }
          
          const entry = dateMap.get(dateKey)!;
          entry.trips = (entry.trips || 0) + item.count;
        });
        
        // Add balance history data
        balanceData.forEach(item => {
          const dateKey = moment(item.date).format('YYYY-MM-DD');
          
          if (!dateMap.has(dateKey)) {
            dateMap.set(dateKey, { date: dateKey, trips: 0, topUps: 0, spending: 0 });
          }
          
          const entry = dateMap.get(dateKey)!;
          
          // Type 'pay' is top-ups (positive amount)
          if (item.type === 'pay') {
            entry.topUps = (entry.topUps || 0) + item.totalAmount;
          }
          
          // Type 'contract' is spending (negative amount, convert to positive for display)
          if (item.type === 'contract') {
            entry.spending = (entry.spending || 0) + Math.abs(item.totalAmount);
          }
        });

        // Add weather data
        console.log('Weather data received:', weatherData.size, 'days');
        weatherData.forEach((weather, dateKey) => {
          if (!dateMap.has(dateKey)) {
            dateMap.set(dateKey, { date: dateKey, trips: 0, topUps: 0, spending: 0 });
          }
          const entry = dateMap.get(dateKey)!;
          entry.temperature = weather.temperature;
          entry.windSpeed = weather.windSpeed;
          console.log(`Added weather for ${dateKey}:`, weather);
        });
        
        // Convert map to array and sort by date
        const chartArray = Array.from(dateMap.values()).sort((a, b) => 
          moment(a.date).unix() - moment(b.date).unix()
        );
        
        setChartData(chartArray);
      } catch (error) {
        console.error('Error fetching chart data:', error);
        setChartData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDailyData();
  }, [dateRange, currentCityId, checkPermission, cities.items]);

  const handlePeriodChange = (type: PeriodType) => {
    setPeriodType(type);
    if (type !== 'custom') {
      setCustomPeriod([null, null]);
    }
  };

  const handleCustomPeriodChange = (dates: DateRange<Moment | null>) => {
    // Check if period exceeds 60 days
    if (dates[0] && dates[1]) {
      const daysDiff = dates[1].diff(dates[0], 'days') + 1;
      if (daysDiff > 60) {
        // Limit to 60 days from start date
        const limitedEndDate = moment(dates[0]).add(59, 'days');
        setCustomPeriod([dates[0], limitedEndDate]);
        setPeriodType('custom');
        return;
      }
      setPeriodType('custom');
    }
    setCustomPeriod(dates);
  };

  const handleLegendClick = (dataKey: string) => {
    setHiddenLines(prev => {
      const newSet = new Set(prev);
      if (newSet.has(dataKey)) {
        newSet.delete(dataKey);
      } else {
        newSet.add(dataKey);
      }
      return newSet;
    });
  };

  // Calculate weekend periods (Friday to Monday) for background highlighting
  const weekendPeriods = useMemo(() => {
    const periods: Array<{ x1: string; x2: string }> = [];
    
    if (chartData.length === 0) return periods;
    
    let weekendStart: string | null = null;
    
    chartData.forEach((dataPoint, index) => {
      const dayOfWeek = moment(dataPoint.date).day(); // 0=Sunday, 1=Monday, ..., 5=Friday, 6=Saturday
      const isWeekend = dayOfWeek === 5 || dayOfWeek === 6 || dayOfWeek === 0 || dayOfWeek === 1; // Fri, Sat, Sun, Mon
      
      if (isWeekend && !weekendStart) {
        // Start of weekend period
        weekendStart = dataPoint.date;
      } else if (!isWeekend && weekendStart) {
        // End of weekend period (at the previous day)
        const prevDate = chartData[index - 1]?.date;
        if (prevDate) {
          periods.push({ x1: weekendStart, x2: prevDate });
        }
        weekendStart = null;
      }
    });
    
    // Handle case where data ends during a weekend
    if (weekendStart) {
      const lastDate = chartData[chartData.length - 1]?.date;
      if (lastDate) {
        periods.push({ x1: weekendStart, x2: lastDate });
      }
    }
    
    return periods;
  }, [chartData]);


  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            backgroundColor: theme.palette.mode === 'light' ? '#FFFFFF' : '#1E1E1E',
            border: `1px solid ${theme.palette.mode === 'light' ? '#E0E0E0' : '#424242'}`,
            borderRadius: '8px',
            padding: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            {payload[0]?.payload?.date}
          </Typography>
          {payload.map((entry: any, index: number) => {
            let value = '';
            if (entry.dataKey === 'trips') {
              value = entry.value?.toString() || '0';
            } else if (entry.dataKey === 'temperature') {
              value = `${entry.value?.toFixed(1)}°C`;
            } else if (entry.dataKey === 'windSpeed') {
              value = `${entry.value?.toFixed(1)} m/s`;
            } else {
              value = `${entry.value?.toFixed(2)} UAH`;
            }
            return (
              <Typography
                key={index}
                variant="body2"
                sx={{ color: entry.color, fontSize: '0.875rem' }}
              >
                {entry.name}: {value}
              </Typography>
            );
          })}
        </Box>
      );
    }
    return null;
  };

  return (
    <Paper
      sx={{
        padding: '24px',
        border: theme.palette.mode === 'light' ? '1px solid rgba(0, 0, 0, 0.08)' : 'none',
        borderRadius: 3,
        minHeight: 450
      }}
    >
      <Stack spacing={2}>
        <Box>
          <Typography
            variant='h6'
            sx={{
              fontWeight: 600,
              fontSize: 20,
              color: theme.palette.mode === 'light' ? '#424242' : '#FFFFFF',
              mb: 0.5
            }}
          >
            {chartTitle}
          </Typography>
          <Typography
            variant='body2'
            sx={{
              color: theme.palette.mode === 'light' ? '#757575' : '#9E9E9E',
              fontSize: '0.875rem'
            }}
          >
            {t('stats.chartDescriptionHint')}
          </Typography>
        </Box>

        {/* Period Selection Buttons and Date Picker */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <ButtonGroup variant="outlined" size="small">
            <Button
              onClick={() => handlePeriodChange('7days')}
              variant={periodType === '7days' ? 'contained' : 'outlined'}
              sx={{
                textTransform: 'none',
                backgroundColor: periodType === '7days' 
                  ? (theme.palette.mode === 'light' ? '#5073F8' : '#5073F8')
                  : 'transparent',
                color: periodType === '7days' 
                  ? '#FFFFFF'
                  : (theme.palette.mode === 'light' ? '#5073F8' : '#90CAF9'),
                borderColor: theme.palette.mode === 'light' ? '#5073F8' : '#90CAF9',
                '&:hover': {
                  backgroundColor: periodType === '7days' 
                    ? (theme.palette.mode === 'light' ? '#3D5DC8' : '#3D5DC8')
                    : 'rgba(80, 115, 248, 0.08)',
                  borderColor: theme.palette.mode === 'light' ? '#5073F8' : '#90CAF9'
                }
              }}
            >
              {t('stats.last7Days')}
            </Button>
            <Button
              onClick={() => handlePeriodChange('30days')}
              variant={periodType === '30days' ? 'contained' : 'outlined'}
              sx={{
                textTransform: 'none',
                backgroundColor: periodType === '30days' 
                  ? (theme.palette.mode === 'light' ? '#5073F8' : '#5073F8')
                  : 'transparent',
                color: periodType === '30days' 
                  ? '#FFFFFF'
                  : (theme.palette.mode === 'light' ? '#5073F8' : '#90CAF9'),
                borderColor: theme.palette.mode === 'light' ? '#5073F8' : '#90CAF9',
                '&:hover': {
                  backgroundColor: periodType === '30days' 
                    ? (theme.palette.mode === 'light' ? '#3D5DC8' : '#3D5DC8')
                    : 'rgba(80, 115, 248, 0.08)',
                  borderColor: theme.palette.mode === 'light' ? '#5073F8' : '#90CAF9'
                }
              }}
            >
              {t('stats.last30Days')}
            </Button>
          </ButtonGroup>

          <DateRangePicker
            startText={t('stats.from')}
            endText={t('stats.to')}
            value={customPeriod}
            onChange={handleCustomPeriodChange}
          />
        </Box>

        <Box sx={{ width: '100%', height: 380, position: 'relative' }}>
          {loading && (
            <Box sx={{ 
              position: 'absolute', 
              top: '50%', 
              left: '50%', 
              transform: 'translate(-50%, -50%)',
              zIndex: 10,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2
            }}>
              <CircularProgress />
              <Typography 
                variant="body2" 
                sx={{ 
                  color: theme.palette.mode === 'light' ? '#757575' : '#9E9E9E',
                  textAlign: 'center',
                  maxWidth: 250
                }}
              >
                {t('stats.waitLoading')}
              </Typography>
            </Box>
          )}
          <ResponsiveContainer width='100%' height='100%'>
            <LineChart 
              data={chartData} 
              margin={{ top: 20, right: 50, left: 20, bottom: 20 }}
            >
              <CartesianGrid 
                strokeDasharray='3 3' 
                stroke={theme.palette.mode === 'light' ? '#E0E0E0' : '#424242'}
                vertical={false}
              />
              {/* Weekend highlighting (Friday to Monday) */}
              {weekendPeriods.map((period, index) => (
                <ReferenceArea
                  key={`weekend-${index}`}
                  x1={period.x1}
                  x2={period.x2}
                  fill={theme.palette.mode === 'light' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(76, 175, 80, 0.15)'}
                  fillOpacity={1}
                />
              ))}
              <XAxis 
                dataKey='date'
                tick={{ 
                  fill: theme.palette.mode === 'light' ? '#616161' : '#B0B0B0',
                  fontSize: 11
                }}
                tickMargin={10}
                axisLine={{ stroke: theme.palette.mode === 'light' ? '#E0E0E0' : '#424242' }}
              />
              <YAxis 
                yAxisId="balance"
                tick={{ 
                  fill: theme.palette.mode === 'light' ? '#616161' : '#B0B0B0',
                  fontSize: 11
                }}
                axisLine={{ stroke: theme.palette.mode === 'light' ? '#E0E0E0' : '#424242' }}
                label={{ 
                  value: t('stats.balanceLabel'), 
                  angle: -90, 
                  position: 'insideLeft',
                  style: { 
                    fill: theme.palette.mode === 'light' ? '#616161' : '#B0B0B0',
                    fontSize: 12
                  }
                }}
              />
              <YAxis 
                yAxisId="trips"
                orientation="right"
                tick={{ 
                  fill: theme.palette.mode === 'light' ? '#616161' : '#B0B0B0',
                  fontSize: 11
                }}
                axisLine={{ stroke: theme.palette.mode === 'light' ? '#E0E0E0' : '#424242' }}
                label={{ 
                  value: t('stats.tripsLabel'), 
                  angle: 90, 
                  position: 'insideRight',
                  style: { 
                    fill: theme.palette.mode === 'light' ? '#616161' : '#B0B0B0',
                    fontSize: 12
                  }
                }}
              />
              <YAxis 
                yAxisId="temperature"
                orientation="right"
                tick={{ 
                  fill: theme.palette.mode === 'light' ? '#616161' : '#B0B0B0',
                  fontSize: 11
                }}
                axisLine={false}
                tickLine={false}
                hide={true}
              />
              <YAxis 
                yAxisId="windSpeed"
                orientation="left"
                tick={{ 
                  fill: theme.palette.mode === 'light' ? '#616161' : '#B0B0B0',
                  fontSize: 11
                }}
                axisLine={false}
                tickLine={false}
                hide={true}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                wrapperStyle={{ 
                  paddingTop: '20px',
                  fontSize: '12px'
                }}
                iconType="line"
                onClick={(e: any) => {
                  if (e && e.dataKey) {
                    handleLegendClick(e.dataKey);
                  }
                }}
                formatter={(value: string, entry: any) => {
                  const isHidden = hiddenLines.has(entry.dataKey);
                  return (
                    <span style={{ 
                      cursor: 'pointer', 
                      textDecoration: isHidden ? 'line-through' : 'none',
                      opacity: isHidden ? 0.5 : 1,
                      userSelect: 'none'
                    }}>
                      {value}
                    </span>
                  );
                }}
              />
              {!hiddenLines.has('trips') && (
                <Line
                  yAxisId="trips"
                  type="monotone"
                  dataKey="trips"
                  name={t('stats.tripsName')}
                  stroke="#5073F8"
                  strokeWidth={2}
                  dot={{ r: 4, fill: '#5073F8' }}
                  activeDot={{ r: 6 }}
                  connectNulls
                />
              )}
              {!hiddenLines.has('topUps') && (
                <Line
                  yAxisId="balance"
                  type="monotone"
                  dataKey="topUps"
                  name={t('stats.topUpName')}
                  stroke="#4CAF50"
                  strokeWidth={2}
                  dot={{ r: 4, fill: '#4CAF50' }}
                  activeDot={{ r: 6 }}
                  connectNulls
                />
              )}
              {!hiddenLines.has('spending') && (
                <Line
                  yAxisId="balance"
                  type="monotone"
                  dataKey="spending"
                  name={t('stats.expenseName')}
                  stroke="#FF9800"
                  strokeWidth={2}
                  dot={{ r: 4, fill: '#FF9800' }}
                  activeDot={{ r: 6 }}
                  connectNulls
                />
              )}
              {!hiddenLines.has('temperature') && (
                <Line
                  yAxisId="temperature"
                  type="monotone"
                  dataKey="temperature"
                  name={t('stats.temperatureName')}
                  stroke="#E91E63"
                  strokeWidth={2}
                  dot={{ r: 3, fill: '#E91E63' }}
                  activeDot={{ r: 5 }}
                  connectNulls
                  strokeDasharray="5 5"
                />
              )}
              {!hiddenLines.has('windSpeed') && (
                <Line
                  yAxisId="windSpeed"
                  type="monotone"
                  dataKey="windSpeed"
                  name={t('stats.windSpeedName')}
                  stroke="#00BCD4"
                  strokeWidth={2}
                  dot={{ r: 3, fill: '#00BCD4' }}
                  activeDot={{ r: 5 }}
                  connectNulls
                  strokeDasharray="3 3"
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Stack>
    </Paper>
  );
};

export default DailyLineChart;
